import { LIST_TABLE_CONTENTS_LABEL, LABEL_COLORS } from '../../constants';
import { SIZE_COVER, SCALE } from '../../constants';
import ListOfAnswersCom from '../common/ListOfAnswersCom';
import PageFooter from '../common/PageFooter';
import CRPreviewSection from '../common/Section';
import bgCover from '@/assets/image/pdfexport/bg-cover.png';
import headImg from '@/assets/image/pdfexport/head-fill.png';
import DoughnutChart from '@/new-components/CRReportPDF/components/DoughnutChart';
import ChartDataLabels from '@energiency/chartjs-plugin-piechart-outlabels';
import { Box, Typography, withStyles } from '@material-ui/core';
import { get, lowerCase } from 'lodash';
import React, { useEffect, useState } from 'react';

const width = SIZE_COVER.width * SCALE;
const height = SIZE_COVER.height * SCALE;

const CustomContainer = withStyles({
  root: {
    padding: `0 ${80 * SCALE}px`,
    height,
    width,
    margin: '0 auto',
    backgroundImage: `url(${bgCover})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    paddingTop: `${64 * SCALE}px`,
  },
})(Box);

const MostSelected = withStyles((theme) => ({
  root: (props) => ({
    fontFamily: 'OpenSans',
    fontWeight: 400,
    fontSize: 12 * SCALE,
    // lineHeight: `${18 * SCALE}px`,
    marginTop: 0,
    marginBottom: props.noMarginBottom ? `${8 * SCALE}px` : `${12 * SCALE}px`, // Conditionally apply margin
    position: 'relative',
    cursor: 'pointer',
    '&:hover::after': {
      content: 'attr(data-tooltip)',
      position: 'absolute',
      bottom: '100%',
      left: '0',
      backgroundColor: '#4a4a4a',
      color: 'white',
      padding: '12px 16px',
      borderRadius: '4px',
      fontSize: '14px',
      whiteSpace: 'normal',
      maxWidth: '300px',
      width: 'max-content',
      zIndex: 1000,
      pointerEvents: 'none',
      marginBottom: '8px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      textAlign: 'left',
      lineHeight: '1.4',
    },
    '&:hover::before': {
      content: '""',
      position: 'absolute',
      bottom: 'calc(100% - 4px)',
      left: '20px',
      border: '4px solid transparent',
      borderTopColor: '#4a4a4a',
      zIndex: 1000,
      pointerEvents: 'none',
    },
  }),
}))(Typography);

const Title = withStyles({
  root: {
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: 20 * SCALE,
    // lineHeight: `${28 * SCALE}px`,
  },
})(Typography);

const Question = withStyles({
  root: {
    fontFamily: 'Lato',
    fontWeight: 600,
    fontSize: 12 * SCALE,
    // lineHeight: `${18 * SCALE}px`,
    marginTop: `${12 * SCALE}px`,
  },
})(Typography);

const Description = withStyles({
  root: {
    fontFamily: 'OpenSans',
    fontWeight: 400,
    fontSize: 12 * SCALE,
    lineHeight: `${18 * SCALE}px`,
    marginTop: `${20 * SCALE}px`,
    marginBottom: `${24 * SCALE}px`,
  },
})(Typography);

const groupByFirstEncounter = (data, key) => {
  return data.reduce((acc, item) => {
    const category = item[key];
    if (!acc.hasOwnProperty(category)) {
      acc[category] = []; // Maintain order by first encounter
    }
    acc[category].push(item);
    return acc;
  }, {});
};

const calculateLifestyleBoxHeight = (answers, options = {}) => {
  const {
    rowHeight = 26, // Height of each row in pixels (based on image)
    rowGap = 2, // Gap between rows in pixels
    lineHeight = 13, // Height per line of text
    maxCharsPerLine = 35, // Maximum characters per line before wrapping
  } = options;

  if (!answers || !Array.isArray(answers) || answers.length === 0) {
    return 0;
  }

  let totalHeight = 0;

  answers.forEach((answer) => {
    const textLength = answer.answer?.length || 0;

    // Calculate how many lines this answer text will take
    const linesNeeded = Math.ceil(textLength / maxCharsPerLine);

    // Use the maximum of base row height or calculated text height
    const answerHeight = Math.max(rowHeight, linesNeeded * lineHeight);

    totalHeight += answerHeight;
  });

  // Add gaps between rows
  totalHeight += (answers.length - 1) * rowGap;

  return totalHeight;
};

const LifestyleQuestionaireFinding = ({
  corporateData,
  setTotalLifeStylePage,
  startPage,
  totalPages,
}) => {
  const [listRandomColors, setListRandomColors] = useState([]);

  const generateRandomColors = (length) => {
    const colors = [];
    for (let i = 0; i < length; i++) {
      // Tạo màu ngẫu nhiên theo định dạng hex (#RRGGBB)
      const color = `#${Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, '0')}`;
      colors.push(color);
    }
    return colors;
  };
  const dataByCategory = groupByFirstEncounter(
    corporateData?.questionaires?.filter((q) => q.answerType !== 'FreeText'),
    'category'
  );

  const categories = Object.keys(dataByCategory);
  const chartPlugins = [ChartDataLabels];
  const backgroundColor = LABEL_COLORS;

  const totalPatientOptOutSurvey =
    get(corporateData, 'overView.totalPatientOptOutSurvey') || '';

  const first2Questions = (() => {
    const firstCategoryQuestions = dataByCategory[categories[0]] || [];
    const questions = firstCategoryQuestions.slice(0, 2);

    // Ensure we always have 2 questions
    while (questions.length < 2) {
      // If we don't have enough questions in first category, look in other categories
      const nextCategoryIndex = questions.length;
      if (categories[nextCategoryIndex]) {
        const nextCategoryQuestions =
          dataByCategory[categories[nextCategoryIndex]] || [];
        if (nextCategoryQuestions.length > 0) {
          questions.push(nextCategoryQuestions[0]);
        }
      } else {
        // If no more categories, create a placeholder question
        questions.push({
          question: 'No additional question available',
          answers: [],
          category: 'No Category',
        });
      }
    }

    return questions.map((question) => {
      const answers = [...question.answers].map((a, i) => ({
        ...a,
        color: LABEL_COLORS[i],
      }));
      question.answers = answers;
      return question;
    });
  })();

  const allQuestions = categories.flatMap((category, index) => {
    const data =
      index === 0
        ? dataByCategory[category]?.slice(2)
        : dataByCategory[category];

    // Filter out questions that are already in first2Questions
    const filteredData =
      data?.filter(
        (question) =>
          !first2Questions.some(
            (firstQ) => firstQ.question === question.question
          )
      ) || [];

    return filteredData.map((question) => {
      const answers = [...question.answers].map((a, i) => ({
        ...a,
        color: LABEL_COLORS[i],
      }));
      question.answers = answers;

      return { question, category };
    });
  });

  const splitLongQuestions = (questions) => {
    const updatedQuestions = [];

    questions.forEach((q) => {
      const { question, category } = q;
      const { answers } = question;

      // Calculate box height for this question
      const boxHeight = calculateLifestyleBoxHeight(answers, {
        rowHeight: 26,
        rowGap: 2,
        lineHeight: 13,
        maxCharsPerLine: 35,
      });
      if (boxHeight > 300) {
        // Split into parts based on height, not answer count
        let currentHeight = 0;
        let currentAnswers = [];
        let partNumber = 1;

        for (let i = 0; i < answers.length; i++) {
          const answer = answers[i];
          const answerHeight = Math.max(
            26,
            Math.ceil(answer.answer?.length / 35) * 13
          );

          if (currentHeight + answerHeight > 520 && currentAnswers.length > 0) {
            // Create a part with current answers
            updatedQuestions.push({
              question: {
                ...question,
                question: `${question.question}`,
                answers: currentAnswers,
              },
              part: partNumber,
              category,
            });
            partNumber++;

            // Reset for next part
            currentAnswers = [answer];
            currentHeight = answerHeight;
          } else {
            currentAnswers.push(answer);
            currentHeight += answerHeight;
            if (i > 0) currentHeight += 2; // Add gap
          }
        }

        // Add the last part if there are remaining answers
        if (currentAnswers.length > 0) {
          updatedQuestions.push({
            question: {
              ...question,
              question: `${question.question}`,
              answers: currentAnswers,
            },
            part: partNumber,
            category,
          });
        }

        // Add the complete question for reference
        updatedQuestions.push({
          question,
          category,
          part: 'All',
        });
      } else {
        updatedQuestions.push(q);
      }
    });

    return updatedQuestions;
  };

  const newQuestions = splitLongQuestions(allQuestions);

  const groupedQuestions = newQuestions
    .filter((it) => it.part !== 'All')
    .reduce((acc, { question, category, part }) => {
      const lastGroup = acc[acc.length - 1];
      if (
        !lastGroup ||
        lastGroup.length === 2 ||
        lastGroup[0]?.single ||
        lastGroup[0]?.part !== part
      ) {
        acc.push([{ question, category, part }]);
      } else {
        lastGroup.push({ question, category, part });
      }
      return acc;
    }, []);

  useEffect(() => {
    setTotalLifeStylePage(groupedQuestions.length + 1);
  }, [groupedQuestions.length, setTotalLifeStylePage]);

  useEffect(() => {
    setListRandomColors(generateRandomColors(100));
  }, []);

  return (
    <>
      <CustomContainer>
        <Box display="flex" flexDirection="column" minHeight="100%">
          <Title>Lifestyle Questionnaire Findings</Title>
          <Description>
            *Lifestyle Survey results for {totalPatientOptOutSurvey}{' '}
            participants are invalid due to incomplete.
          </Description>

          {first2Questions.map((question, index) => {
            const mostSelected = question.answers?.reduce(
              (max, item) =>
                item.total > (max?.total ?? -Infinity) ? item : max,
              0
            );
            const chartData = question.answers.map((it) =>
              it.percent?.formatPercentValue()
            );
            const isYesNo = question?.answers.every((item) =>
              ['no', 'yes', 'other'].includes(lowerCase(item.answer))
            );

            // Only show category header if it's different from the previous question
            const showCategoryHeader =
              index === 0 ||
              first2Questions[index - 1].category !== question.category;

            return (
              <>
                {showCategoryHeader && (
                  <Box>
                    <CRPreviewSection title={question.category} img={headImg} />
                  </Box>
                )}
                <Question>
                  {new DOMParser().parseFromString(
                    question?.question,
                    'text/html'
                  ).body.textContent || ''}
                </Question>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  mt={2 * SCALE}
                  mb={4.4 * SCALE}
                >
                  <Box width={`${380 * SCALE}px`} height={`${200 * SCALE}px`}>
                    <DoughnutChart
                      chartData={chartData}
                      chartPlugins={chartPlugins}
                      backgroundColor={backgroundColor}
                      animationDuration={400}
                      isLabelOutside={true}
                    />
                    {!isYesNo && (
                      <Box display="flex" flexDirection="column">
                        <MostSelected
                          data-tooltip={`${mostSelected?.percent?.formatPercentValue()}% answered '${
                            mostSelected?.answer
                          }'.`}
                        >
                          {mostSelected?.percent?.formatPercentValue()}%
                          answered '
                          {mostSelected?.answer?.length > 78
                            ? `${mostSelected?.answer.slice(0, 75)}...`
                            : mostSelected?.answer}
                          '.
                        </MostSelected>
                      </Box>
                    )}
                  </Box>
                  <Box ml={`${4 * SCALE}px`} mt={`${3 * SCALE}px`}>
                    {isYesNo && (
                      <MostSelected
                        noMarginBottom
                        data-tooltip={`${mostSelected?.percent?.formatPercentValue()}% answered '${
                          mostSelected?.answer
                        }'.`}
                      >
                        {mostSelected?.percent?.formatPercentValue()}% answered
                        '{mostSelected?.answer}
                        '.
                      </MostSelected>
                    )}
                    <ListOfAnswersCom
                      data={question?.answers}
                      colors={backgroundColor}
                    />
                  </Box>
                </Box>
              </>
            );
          })}
          <PageFooter
            page={startPage}
            totalPages={totalPages}
            title={LIST_TABLE_CONTENTS_LABEL.LifestyleQuestionnaireFindings}
          />
        </Box>
      </CustomContainer>

      {groupedQuestions.map((pair, pageIndex) => {
        return (
          <CustomContainer key={`container-${pageIndex}`}>
            <Box display="flex" flexDirection="column" minHeight="100%">
              {pair.map(({ question, category, part }, qIndex) => {
                const showCategoryTitle =
                  qIndex === 0 || pair[qIndex - 1].category !== category;
                const isYesNo = question?.answers.every((item) =>
                  ['no', 'yes', 'other'].includes(lowerCase(item.answer))
                );
                const filtered = newQuestions.find(
                  (q) =>
                    question.question === q.question.question &&
                    q.part === 'All'
                );

                return (
                  <React.Fragment key={`question-${pageIndex}-${qIndex}`}>
                    {(showCategoryTitle ||
                      (filtered &&
                        filtered?.question?.question === question.question &&
                        part === 1)) && (
                      <Box mb={1 * SCALE}>
                        <CRPreviewSection title={category} img={headImg} />
                      </Box>
                    )}
                    {filtered && part > 1 ? null : (
                      <Question>
                        {new DOMParser().parseFromString(
                          question?.question,
                          'text/html'
                        ).body.textContent || ''}
                      </Question>
                    )}

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      mt={
                        filtered?.question?.question === question.question &&
                        part > 1
                          ? 0.5 * SCALE
                          : 1 * SCALE
                      }
                      mb={
                        filtered?.question?.question === question.question
                          ? 0
                          : 5.6 * SCALE
                      }
                    >
                      <Box
                        width={`${400 * SCALE}px`}
                        height={`${200 * SCALE}px`}
                      >
                        {filtered?.question?.question === question.question ? (
                          part > 1 ? null : (
                            <DoughnutChart
                              chartData={filtered.question.answers.map((it) =>
                                it.percent?.formatPercentValue()
                              )}
                              chartPlugins={chartPlugins}
                              backgroundColor={backgroundColor}
                              animationDuration={400}
                              isLabelOutside={true}
                            />
                          )
                        ) : (
                          <DoughnutChart
                            chartData={question.answers.map((it) =>
                              it.percent?.formatPercentValue()
                            )}
                            chartPlugins={chartPlugins}
                            backgroundColor={backgroundColor}
                            animationDuration={400}
                            isLabelOutside={true}
                          />
                        )}

                        {!isYesNo &&
                          (filtered ? (
                            part === 1 && (
                              <MostSelected
                                data-tooltip={`${filtered?.question?.answers
                                  .reduce(
                                    (max, item) =>
                                      item.total > (max?.total ?? -Infinity)
                                        ? item
                                        : max,
                                    0
                                  )
                                  ?.percent?.formatPercentValue()}% answered '${
                                  filtered?.question?.answers?.find(
                                    (a) =>
                                      a.total ===
                                      Math.max(
                                        ...filtered?.question?.answers.map(
                                          (a) => a.total
                                        )
                                      )
                                  )?.answer
                                }'.`}
                              >
                                {filtered?.question?.answers
                                  .reduce(
                                    (max, item) =>
                                      item.total > (max?.total ?? -Infinity)
                                        ? item
                                        : max,
                                    0
                                  )
                                  ?.percent?.formatPercentValue()}
                                % answered '
                                {
                                  filtered?.question?.answers?.find(
                                    (a) =>
                                      a.total ===
                                      Math.max(
                                        ...filtered?.question?.answers.map(
                                          (a) => a.total
                                        )
                                      )
                                  )?.answer
                                }
                                '.
                              </MostSelected>
                            )
                          ) : (
                            <MostSelected
                              data-tooltip={`${question.answers
                                .reduce(
                                  (max, item) =>
                                    item.total > (max?.total ?? -Infinity)
                                      ? item
                                      : max,
                                  0
                                )
                                ?.percent?.formatPercentValue()}% answered '${
                                question.answers?.find(
                                  (a) =>
                                    a.total ===
                                    Math.max(
                                      ...question.answers.map((a) => a.total)
                                    )
                                )?.answer
                              }'.`}
                            >
                              {question.answers
                                .reduce(
                                  (max, item) =>
                                    item.total > (max?.total ?? -Infinity)
                                      ? item
                                      : max,
                                  0
                                )
                                ?.percent?.formatPercentValue()}
                              % answered '
                              {
                                question.answers?.find(
                                  (a) =>
                                    a.total ===
                                    Math.max(
                                      ...question.answers.map((a) => a.total)
                                    )
                                )?.answer
                              }
                              '.
                            </MostSelected>
                          ))}
                      </Box>
                      <Box
                        ml={`${4 * SCALE}px`}
                        mt={
                          filtered &&
                          filtered?.question?.question === question.question &&
                          part > 1
                            ? 0
                            : 2 * SCALE
                        }
                      >
                        {isYesNo && (
                          <MostSelected
                            noMarginBottom
                            data-tooltip={`${question.answers
                              .reduce(
                                (max, item) =>
                                  item.total > (max?.total ?? -Infinity)
                                    ? item
                                    : max,
                                0
                              )
                              ?.percent?.formatPercentValue()}% answered '${
                              question.answers?.find(
                                (a) =>
                                  a.total ===
                                  Math.max(
                                    ...question.answers.map((a) => a.total)
                                  )
                              )?.answer
                            }'.`}
                          >
                            {question.answers
                              .reduce(
                                (max, item) =>
                                  item.total > (max?.total ?? -Infinity)
                                    ? item
                                    : max,
                                0
                              )
                              ?.percent?.formatPercentValue()}
                            % answered '
                            {
                              question.answers?.find(
                                (a) =>
                                  a.total ===
                                  Math.max(
                                    ...question.answers.map((a) => a.total)
                                  )
                              )?.answer
                            }
                            '.
                          </MostSelected>
                        )}
                        <ListOfAnswersCom data={question?.answers} />
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              })}
              <PageFooter
                page={startPage + 1 + pageIndex}
                totalPages={totalPages}
                title={LIST_TABLE_CONTENTS_LABEL.LifestyleQuestionnaireFindings}
              />
            </Box>
          </CustomContainer>
        );
      })}
    </>
  );
};

export default LifestyleQuestionaireFinding;
